import React, { useCallback, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import { useCreateMigrationBatch } from '../../../../../api/documentVault'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { useCheckPolicy } from '../../../../../hooks'
import Card from '../../../../molecules/Card'
import MigrationBatchDetail from './MigrationBatchDetail'
import MigrationBatchList from './MigrationBatchList'

const VaultMigrationAdmin = () => {
  const canManage = useCheckPolicy('danger_zone')
  const history = useHistory()
  const location = useLocation()
  const [orionFirmId, setOrionFirmId] = useState('')
  const [createdBatch, setCreatedBatch] = useState(null)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(null)

  const activeBatchId = useMemo(() => {
    const parts = location.pathname.split('/')
    const migrationIndex = parts.indexOf('migration')
    const batchIdStr = migrationIndex >= 0 ? parts[migrationIndex + 1] : null
    return batchIdStr && /^\d+$/.test(batchIdStr) ? Number(batchIdStr) : null
  }, [location.pathname])

  const queryClient = useQueryClient()
  const createBatch = useCreateMigrationBatch()

  const handleCreate = useCallback(async () => {
    const trimmed = String(orionFirmId).trim()
    if (!trimmed) return
    if (!/^\d+$/.test(trimmed)) {
      setError('Orion Firm ID must be a numeric value')
      return
    }
    setError(null)
    setCreatedBatch(null)
    try {
      const result = await createBatch.mutateAsync({
        orionFirmId: Number(trimmed)
      })
      setCreatedBatch(result)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.migrationBatches] })
    } catch (err) {
      setError(err?.response?.detail || err?.response?.data?.detail || err?.response?.data?.routeData?.message || err?.message || 'Failed to create migration batch')
    }
  }, [orionFirmId, createBatch, queryClient])

  const handleCopy = useCallback(async (url, label) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(label)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      setError('Failed to copy to clipboard')
    }
  }, [])

  const handleViewBatch = useCallback((batchId) => {
    history.push(`/admin/vault/migration/${batchId}`)
  }, [history])

  const handleBack = useCallback(() => {
    setCreatedBatch(null)
    history.push('/admin/vault/migration')
  }, [history])

  if (activeBatchId) {
    return (
      <div className='flex flex-col gap-lg' style={{ padding: '1rem 0' }}>
        <div>
          <SydButton variant='outline' size='sm' icon='arrowBack' onClick={handleBack}>
            Back to Migrations
          </SydButton>
        </div>
        <MigrationBatchDetail batchId={activeBatchId} />
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-lg' style={{ padding: '1rem 0' }}>
      {canManage && (
        <Card>
          <SydLabel label='Create New Migration' />
          <div className='flex flex-row items-end gap-sm'>
            <SydInput
              placeholder='Orion Firm ID'
              value={orionFirmId}
              onChange={(e) => setOrionFirmId(e.target.value)}
              size='sm'
            />
            <SydButton
              variant='primary'
              onClick={handleCreate}
              processing={createBatch.isLoading}
              disabled={!orionFirmId || createBatch.isLoading}
            >
              Create Migration
            </SydButton>
          </div>
          {error && <div className='font-size-sm' style={{ color: 'var(--color-danger-500)', marginTop: '8px' }}>{error}</div>}

          {createdBatch && (
            <div className='flex flex-col gap-md border border-neutral-300 bg-neutral-100 p-4 rounded mt-4'>
              <div className='font-weight-bold opacity-lowlighted'>
                Batch #{createdBatch.batchId} Created - Upload Files
              </div>
              <div className='font-size-sm'>
                Upload links are valid for 24 hours. Please upload the required files to the corresponding URLs to start the migration process.
              </div>

              <div className='flex gap-sm'>
                <div className='flex flex-col gap-xs'>
                  <SydLabel label='Mapping File (mapping.json)' />
                  <div className='font-size-xs border rounded p-2 border-neutral-300 bg-neutral-050'>
                    {createdBatch.uploadUrls.mapping}
                  </div>
                  <SydButton
                    size='sm' variant='outline'
                    onClick={() => handleCopy(createdBatch.uploadUrls.mapping, 'mapping')}
                  >
                    {copied === 'mapping' ? 'Copied!' : 'Copy Mapping URL'}
                  </SydButton>
                </div>

                <div className='flex flex-col gap-xs'>
                  <SydLabel label='Upload File (upload.zip)' />
                  <div className='font-size-xs border rounded p-2 border-neutral-300 bg-neutral-050'>
                    {createdBatch.uploadUrls.zip}
                  </div>
                  <SydButton
                    size='sm' variant='outline'
                    onClick={() => handleCopy(createdBatch.uploadUrls.zip, 'zip')}
                  >
                    {copied === 'zip' ? 'Copied!' : 'Copy Zip URL'}
                  </SydButton>
                </div>
              </div>

              <SydButton variant='primary' onClick={() => handleViewBatch(createdBatch.batchId)}>
                View Batch Details
              </SydButton>
            </div>
          )}
        </Card>
      )}

      <MigrationBatchList onSelectBatch={handleViewBatch} />
    </div>
  )
}

export default VaultMigrationAdmin
